import React, { useState, useEffect, useContext } from "react";
import ApiURL from "../Api/Api";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { SidebarContext } from "./SidebarContext";
import Sidebar from "./Sidebar";
import Navbar from "./Navbar";
import {
  PencilIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  CalendarIcon,
  TagIcon,
  UserIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";

/* ═══════════════════════════════════════════════════════════════════════ */
const AttributeReport = () => {
  const [attributes, setAttributes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editAttribute, setEditAttribute] = useState(null);
  const [editForm, setEditForm] = useState({ main_head_name: "", sub_main_name: "", end_date: "" });
  const [isEditLoading, setIsEditLoading] = useState(false);
  const navigate = useNavigate();
  const companyId = localStorage.getItem("company_id") || "";
  const currentUserName = localStorage.getItem("user_name") || "admin";
  const { isSidebarOpen } = useContext(SidebarContext);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${ApiURL}/getAttributes.php?company_id=${companyId}`);
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const data = await response.json();
      if (data?.status && Array.isArray(data.attributes)) {
        setAttributes(data.attributes);
      } else {
        throw new Error(data?.message || "Failed to fetch attributes");
      }
    } catch (error) {
      console.error("API Error:", error);
      setError(`Network error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const openEditModal = (attribute) => {
    setEditAttribute(attribute);
    setEditForm({
      main_head_name: attribute.main_head_name || "",
      sub_main_name: attribute.sub_main_name || "",
      end_date: attribute.end_date || "",
    });
  };

  const closeEditModal = () => {
    setEditAttribute(null);
    setEditForm({ main_head_name: "", sub_main_name: "", end_date: "" });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setIsEditLoading(true);
    try {
      const response = await fetch(`${ApiURL}/EditAttribute.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          s_no: editAttribute.s_no,
          main_head_name: editForm.main_head_name,
          sub_main_name: editForm.sub_main_name,
          end_date: editForm.end_date,
          updated_by: currentUserName,
          company_id: companyId,
        }),
      });

      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const result = await response.json();

      if (result.status) {
        toast.success(result.message || "Attribute updated successfully!");
        await fetchData(); // Refresh table
        setTimeout(closeEditModal, 500); // Close modal after success
      } else {
        throw new Error(result.message || "Failed to update attribute");
      }
    } catch (error) {
      console.error("Edit Error:", error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsEditLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f6fa]">
      <Navbar />
      <div className="flex pt-16">
        <Sidebar />
        <main className={`flex-1 transition-all duration-300 w-full ${isSidebarOpen ? 'lg:ml-72' : ''}`}>
          <div className="w-full max-w-7xl mx-auto px-3 py-4 sm:px-5 sm:py-6 pb-16">

            {/* ── PAGE HEADER ── */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
              className="bg-white rounded-2xl border border-slate-200 mb-5 shadow-sm overflow-hidden"
            >
              <div className="h-[2px] bg-gradient-to-r from-indigo-500 to-emerald-500" />
              <div className="px-5 py-5 sm:px-6 sm:py-6 flex items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                    <p className="text-[9px] font-extrabold uppercase tracking-[0.25em] text-indigo-600">Reports</p>
                  </div>
                  <h1 className="text-xl sm:text-2xl font-black text-slate-800 tracking-tight leading-none">Attribute Report</h1>
                  <p className="text-[11px] text-slate-400 mt-1.5 font-medium">
                    View and manage transaction attributes.
                  </p>
                </div>
                {/* Stats pill */}
                <div className="flex-shrink-0 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-center">
                  <p className="text-[9px] font-extrabold text-slate-400 uppercase tracking-wider mb-0.5">Total Attributes</p>
                  <p className="text-2xl font-black text-slate-800 leading-none">{attributes.length}</p>
                </div>
              </div>
            </motion.div>

            {/* ── TABLE SECTION ── */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.08, duration: 0.32 }}
              className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm"
            >
              <div className="h-[2px] bg-gradient-to-r from-slate-300 via-slate-400 to-slate-500" />
              <div className="px-4 pt-4 pb-5 sm:px-6 sm:pt-5">

                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-[9px] font-extrabold text-slate-400 uppercase tracking-[0.18em] mb-0.5">Registry</p>
                    <h2 className="text-[13px] font-black text-slate-700">All Attributes</h2>
                  </div>
                  <button
                    onClick={fetchData}
                    disabled={isLoading}
                    className="w-8 h-8 flex items-center justify-center bg-white border border-slate-200 text-slate-500 hover:text-indigo-600 hover:border-indigo-300 rounded-lg transition-all touch-manipulation disabled:opacity-40"
                    aria-label="Refresh"
                  >
                    <ArrowPathIcon className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
                  </button>
                </div>

                {/* Loading State */}
                {isLoading && (
                  <div className="space-y-2">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="bg-slate-50 rounded-xl border border-slate-200 h-12 overflow-hidden">
                        <div className="h-full p-3 flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-slate-200 animate-pulse" />
                          <div className="flex-1 space-y-1.5">
                            <div className="h-2 w-24 bg-slate-200 rounded animate-pulse" />
                            <div className="h-2 w-32 bg-slate-200 rounded animate-pulse" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Error State */}
                {error && !isLoading && (
                  <div className="flex flex-col items-center justify-center py-12 gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-rose-50 border border-rose-200 flex items-center justify-center">
                      <ExclamationTriangleIcon className="w-6 h-6 text-rose-500" />
                    </div>
                    <p className="text-[12px] text-rose-500 font-medium">{error}</p>
                    <button
                      onClick={fetchData}
                      className="text-[11px] font-bold text-indigo-600 hover:text-indigo-700"
                    >
                      Try Again
                    </button>
                  </div>
                )}

                {/* Table - Responsive with horizontal scroll */}
                {!isLoading && !error && (
                  <>
                    {attributes.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-12 gap-3">
                        <div className="w-12 h-12 rounded-2xl bg-slate-100 border border-slate-200 flex items-center justify-center">
                          <DocumentTextIcon className="w-6 h-6 text-slate-300" />
                        </div>
                        <p className="text-[12px] text-slate-400 font-medium">No attributes found</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto rounded-xl border border-slate-200">
                        <div className="min-w-[800px]">
                          <table className="w-full text-left text-[11px]">
                            <thead>
                              <tr className="bg-slate-50 border-b border-slate-200">
                                {[
                                  '#', 'Main Head', 'Sub Head', 'Created Date', 'Created By',
                                  'Start Date', 'End Date', 'Updated By', 'Updated Date', 'Action'
                                ].map(h => (
                                  <th key={h} className="px-3 py-3 text-[9px] font-extrabold text-slate-500 uppercase tracking-[0.18em] whitespace-nowrap">
                                    {h}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {attributes.map((attribute, index) => (
                                <motion.tr
                                  key={attribute.s_no}
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  transition={{ delay: index * 0.02 }}
                                  className={`hover:bg-slate-50/80 transition-colors group ${
                                    attribute.updated_date ? 'bg-emerald-50/30' : ''
                                  }`}
                                >
                                  <td className="px-3 py-2.5">
                                    <span className="text-[10px] font-extrabold text-slate-400">
                                      {String(index + 1).padStart(2, '0')}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2.5 font-bold text-slate-700 group-hover:text-indigo-600 transition-colors whitespace-nowrap">
                                    {attribute.main_head_name || '-'}
                                  </td>
                                  <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">
                                    {attribute.sub_main_name || '-'}
                                  </td>
                                  <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">
                                    {attribute.created_date || '-'}
                                  </td>
                                  <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">
                                    {attribute.created_by || '-'}
                                  </td>
                                  <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">
                                    {attribute.start_date || '-'}
                                  </td>
                                  <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">
                                    {attribute.end_date || '-'}
                                  </td>
                                  <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">
                                    {attribute.updated_by || '-'}
                                  </td>
                                  <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">
                                    {attribute.updated_date || '-'}
                                  </td>
                                  <td className="px-3 py-2.5">
                                    <button
                                      onClick={() => openEditModal(attribute)}
                                      className="flex items-center gap-1 px-2.5 py-1.5 bg-indigo-50 border border-indigo-200 text-indigo-600 text-[10px] font-bold rounded-lg hover:bg-indigo-100 hover:border-indigo-300 transition-all touch-manipulation"
                                    >
                                      <PencilIcon className="w-3 h-3" />
                                      Edit
                                    </button>
                                  </td>
                                </motion.tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Footer count */}
                    {attributes.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
                        <span className="text-[10px] text-slate-400 font-medium">
                          Showing <span className="text-slate-600 font-bold">{attributes.length}</span> attribute{attributes.length !== 1 ? 's' : ''}
                        </span>
                        <div className="flex items-center gap-1.5">
                          <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                          <span className="text-[9px] font-extrabold text-emerald-600 uppercase tracking-wider">Updated entries highlighted</span>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </motion.div>

            {/* Mobile bottom spacer */}
            <div className="h-8 sm:hidden" />
          </div>
        </main>
      </div>

      {/* ── EDIT MODAL ── */}
      <AnimatePresence>
        {editAttribute && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={(e) => e.target === e.currentTarget && closeEditModal()}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-xl border border-slate-100 p-6 w-full max-w-md"
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-[9px] font-extrabold text-indigo-600 uppercase tracking-[0.18em] mb-0.5">Edit</p>
                  <h2 className="text-base font-bold text-slate-800">Edit Attribute</h2>
                </div>
                <button
                  onClick={closeEditModal}
                  className="w-7 h-7 rounded-lg bg-slate-100 text-slate-500 hover:bg-slate-200 flex items-center justify-center transition-all"
                >
                  <XMarkIcon className="w-3.5 h-3.5" />
                </button>
              </div>

              <form onSubmit={handleEditSubmit}>
                {/* Main Head Name */}
                <div className="mb-4">
                  <label className="text-[10px] font-extrabold text-slate-500 uppercase tracking-[0.18em] block mb-1.5">
                    Main Head Name <span className="text-rose-400">*</span>
                  </label>
                  <div className="relative">
                    <TagIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                    <input
                      type="text"
                      name="main_head_name"
                      value={editForm.main_head_name}
                      onChange={handleEditChange}
                      className="w-full h-9 pl-8 pr-3 text-[12px] font-medium text-slate-700 bg-white border border-slate-200 rounded-lg outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all"
                      required
                    />
                  </div>
                </div>

                {/* Sub Main Name */}
                <div className="mb-4">
                  <label className="text-[10px] font-extrabold text-slate-500 uppercase tracking-[0.18em] block mb-1.5">
                    Sub Head Name
                  </label>
                  <div className="relative">
                    <DocumentTextIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                    <input
                      type="text"
                      name="sub_main_name"
                      value={editForm.sub_main_name}
                      onChange={handleEditChange}
                      className="w-full h-9 pl-8 pr-3 text-[12px] font-medium text-slate-700 bg-white border border-slate-200 rounded-lg outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all"
                    />
                  </div>
                </div>

                {/* End Date */}
                <div className="mb-5">
                  <label className="text-[10px] font-extrabold text-slate-500 uppercase tracking-[0.18em] block mb-1.5">
                    End Date <span className="text-rose-400">*</span>
                  </label>
                  <div className="relative">
                    <CalendarIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                    <input
                      type="date"
                      name="end_date"
                      value={editForm.end_date}
                      onChange={handleEditChange}
                      className="w-full h-9 pl-8 pr-3 text-[12px] font-medium text-slate-700 bg-white border border-slate-200 rounded-lg outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all"
                      required
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={closeEditModal}
                    className="flex-1 h-9 rounded-lg border border-slate-200 text-slate-600 text-[11px] font-bold hover:bg-slate-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isEditLoading}
                    className="flex-1 h-9 rounded-lg bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white text-[11px] font-extrabold transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
                  >
                    {isEditLoading ? (
                      <>
                        <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <CheckCircleIcon className="w-3.5 h-3.5" />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <ToastContainer position="top-right" autoClose={3000} theme="light" />
    </div>
  );
};

export default AttributeReport;